/**
 * Elementary Plugin
 *
 * Provides Elementary Audio synth nodes.
 * Depends on Audio Base Plugin for AudioEndpoint.
 *
 * Elementary uses the WebRenderer which requires:
 * - Browser: standardized-audio-context with standardized-audio-worklet
 * - Node.js: node-web-audio-api (AudioWorklet support)
 *
 * The core RuntimeEnv provides the appropriate AudioContext.
 */

import type { Plugin } from '@plugins/Plugin';
import { BaseDependencies } from '@/nodes/Dependencies';
import type { AudioDependencies } from '@audio/nodes/AudioBaseNode';
import type { INode } from '@nodes/INode';

// Elementary nodes
import { elementarySynthNodeDef } from '@elementary/nodes/ElementarySynthNode/ElementarySynthNode';
import { elementaryFMSynthNodeDef } from '@elementary/nodes/ElementaryFMSynthNode/ElementaryFMSynthNode';

const namespace = 'elem';

const nodes = [
  elementarySynthNodeDef,
  elementaryFMSynthNodeDef
];

export const elementaryPlugin: Plugin = {
  id: 'elementary',
  name: 'Elementary Audio',
  namespace,
  nodes,

  createNode(type: string, id: string, deps: BaseDependencies): INode {
    const nodeDef = nodes.find(n => n.type === type);
    if (!nodeDef) {
      throw new Error(`Unknown node type: ${type}`);
    }
    return new nodeDef.ctor(id, { ...deps, type: `${namespace}.${type}` } as AudioDependencies);
  },

  async registerUI({ registerViz }) {
    // Elementary synths use envelope visualization from audio plugin
    const { EnvelopeVisualization } = await import('../audio/ui/EnvelopeVisualization');
    registerViz('Synth', EnvelopeVisualization);
    registerViz('FMSynth', EnvelopeVisualization);
  }
};
